// BOORO: the knowledge area. The handbook and what the company knows, as
// pages of Markdown that link to each other, and files such as logos, in
// folders. A page or file lies in the whole account or in one office; who may
// read it is a line of receivers above it.

import { insertWikiLink, linkedPage, mapPageLinks, wikiLinkQueryAt } from "@booro/knowledge";
import { orderedGroups } from "@booro/team";
import { Trans, useLingui } from "@lingui/react/macro";
import { ChatMarkdown, mentionHref } from "@rakazo/chat-ui/web";
import type {
  AccessSubject,
  KnowledgeFileSummary,
  KnowledgeFolder,
  KnowledgePage,
  KnowledgePageSummary,
  OrgSnapshot,
} from "@rakazo/contracts";
import {
  BotAvatar,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Input,
  Textarea,
} from "@rakazo/ui-web";
import type { LucideIcon } from "lucide-react";
import {
  Building,
  Eye,
  Globe,
  Link2,
  Lock,
  Pencil,
  Plus,
  Search,
  Trash2,
  Undo2,
  UserRound,
  Users,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { rpc } from "../../lib/rpc";
import { errorText } from "../../lib/user-error";
import { KnowledgeFileView } from "./knowledge-file";
import type { KnowledgeItem } from "./knowledge-tree";
import { KnowledgeTree, MoveMenu } from "./knowledge-tree";
import { ScopeFilter, useOrgScope, useOrgSnapshot } from "./org-scope";
import type { WorkspaceChrome } from "./workspace-header";
import { WorkspaceViewHeader } from "./workspace-header";

/** The scaffold of a new page. */
function scaffold(title: string): string {
  return `# ${title}\n\n## Zweck\n\n## Inhalt\n`;
}

/** How far a page reaches, as one sign: the widest of its receivers. */
export function pageReach(receivers: readonly AccessSubject[]): AccessSubject["kind"] | "none" {
  for (const kind of ["account", "space", "group", "bot"] as const) {
    if (receivers.some((receiver) => receiver.kind === kind)) return kind;
  }
  return "none";
}

const REACH_ICONS: Record<AccessSubject["kind"] | "none", LucideIcon> = {
  account: Globe,
  space: Building,
  group: Users,
  bot: UserRound,
  none: Lock,
};

function useReachLabels(): Record<AccessSubject["kind"] | "none", string> {
  const { t } = useLingui();
  return {
    account: t`Whole account`,
    space: t`Whole office`,
    group: t({ message: "Groups", context: "organigram" }),
    bot: t`Single agents`,
    none: t`Nobody`,
  };
}

/** The name of a receiver, as the line above a page shows it. */
function useReceiverName(snapshot: OrgSnapshot | null) {
  const { t } = useLingui();
  return useCallback(
    (receiver: AccessSubject): string => {
      if (receiver.kind === "account") return t`Whole account`;
      if (receiver.kind === "space") {
        return snapshot?.offices.find((office) => office.id === receiver.id)?.name ?? t`Office`;
      }
      if (receiver.kind === "group") {
        return (
          snapshot?.groups.find((group) => group.id === receiver.id)?.name ??
          t({ message: "Group", context: "organigram" })
        );
      }
      return snapshot?.agents.find((agent) => agent.id === receiver.id)?.name ?? t`Agent`;
    },
    [snapshot, t],
  );
}

export function KnowledgeView({ title, chrome }: { title: string; chrome: WorkspaceChrome }) {
  const { t } = useLingui();
  const { snapshot, error: orgError } = useOrgSnapshot();
  const [scope, setScope] = useOrgScope();
  const reachLabels = useReachLabels();
  const [pages, setPages] = useState<KnowledgePageSummary[]>([]);
  const [files, setFiles] = useState<KnowledgeFileSummary[]>([]);
  const [folders, setFolders] = useState<KnowledgeFolder[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // The open file; a page and a file are never open at once.
  const [fileId, setFileId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(0);
  const [page, setPage] = useState<KnowledgePage | null>(null);
  const [pageTitle, setPageTitle] = useState("");
  const [draft, setDraft] = useState("");
  // BOORO: pages open as a reading view; only a new page starts in the editor.
  const [mode, setMode] = useState<"write" | "read">("read");
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggestionQuery, setSuggestionQuery] = useState<string | null>(null);
  const [agentPages, setAgentPages] = useState<Set<string> | null>(null);
  const [agentFiles, setAgentFiles] = useState<Set<string> | null>(null);
  const loadedRef = useRef(false);
  const editorRef = useRef<HTMLTextAreaElement | null>(null);
  const selectedIdRef = useRef<string | null>(null);
  const pageRef = useRef<KnowledgePage | null>(null);
  const titleRef = useRef("");
  const draftRef = useRef("");
  const savedRef = useRef<{ pageId: string; title: string; content: string } | null>(null);
  const loadTokenRef = useRef(0);
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const pendingSavesRef = useRef(0);

  const reload = useCallback(async () => {
    try {
      const [next, nextFiles, nextFolders] = await Promise.all([
        rpc.booroKnowledge.pages(),
        rpc.booroKnowledge.files(),
        rpc.booroKnowledge.folders(),
      ]);
      setPages(next);
      setFiles(nextFiles);
      setFolders(nextFolders);
      setError("");
      // The first page opens once; later reloads keep what the person chose.
      if (!loadedRef.current) {
        loadedRef.current = true;
        setSelectedId((current) => current ?? next[0]?.id ?? null);
      }
    } catch (cause) {
      setError(errorText(cause));
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  // "What can Lea read?": with an agent in the filter, only its pages are shown.
  useEffect(() => {
    if (!scope.botId) {
      setAgentPages(null);
      setAgentFiles(null);
      return;
    }
    let active = true;
    void Promise.all([
      rpc.booroKnowledge.forAgent({ botId: scope.botId }),
      rpc.booroKnowledge.filesForAgent({ botId: scope.botId }),
    ])
      .then(([nextPages, nextFiles]) => {
        if (!active) return;
        setAgentPages(new Set(nextPages.map((entry) => entry.id)));
        setAgentFiles(new Set(nextFiles.map((entry) => entry.id)));
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [scope.botId]);

  const loadPage = useCallback(async (pageId: string) => {
    const token = ++loadTokenRef.current;
    try {
      const next = await rpc.booroKnowledge.page({ pageId });
      if (token !== loadTokenRef.current) return;
      pageRef.current = next;
      savedRef.current = next
        ? { pageId: next.id, title: next.title, content: next.content }
        : null;
      titleRef.current = next?.title ?? "";
      draftRef.current = next?.content ?? "";
      setPage(next);
      setPageTitle(next?.title ?? "");
      setDraft(next?.content ?? "");
      setError("");
    } catch (cause) {
      if (token === loadTokenRef.current) setError(errorText(cause));
    }
  }, []);

  useEffect(() => {
    if (!selectedId) {
      loadTokenRef.current += 1;
      pageRef.current = null;
      savedRef.current = null;
      titleRef.current = "";
      draftRef.current = "";
      setPage(null);
      return;
    }
    void loadPage(selectedId);
  }, [selectedId, loadPage]);

  /** The office the filter points at, through a group or an agent if need be. */
  const scopeOffice = useMemo(() => {
    if (scope.spaceId) return scope.spaceId;
    if (scope.groupId) {
      return snapshot?.groups.find((group) => group.id === scope.groupId)?.spaceId ?? null;
    }
    if (scope.botId) {
      return snapshot?.agents.find((agent) => agent.id === scope.botId)?.spaceId ?? null;
    }
    return null;
  }, [scope, snapshot]);

  const inScope = useCallback(
    (spaceId: string | null) => !scopeOffice || spaceId === null || spaceId === scopeOffice,
    [scopeOffice],
  );
  const filtered = useMemo(
    () =>
      pages.filter((entry) => (!agentPages || agentPages.has(entry.id)) && inScope(entry.spaceId)),
    [pages, agentPages, inScope],
  );
  const filteredFiles = useMemo(
    () =>
      files.filter((entry) => (!agentFiles || agentFiles.has(entry.id)) && inScope(entry.spaceId)),
    [files, agentFiles, inScope],
  );
  // With an agent in the filter, only folders that hold something it reads remain.
  const filteredFolders = useMemo(() => {
    const visible = folders.filter((folder) => inScope(folder.spaceId));
    if (!scope.botId) return visible;
    const used = new Set(
      [...filtered, ...filteredFiles].map((entry) => `${entry.spaceId}:${entry.folder}`),
    );
    return visible.filter((folder) =>
      [...used].some(
        (key) =>
          key === `${folder.spaceId}:${folder.path}` ||
          key.startsWith(`${folder.spaceId}:${folder.path}/`),
      ),
    );
  }, [folders, filtered, filteredFiles, inScope, scope.botId]);

  /**
   * The places pages lie in: the account first, then each office. With one office
   * the two are the same to the person: the office stands alone, without a name,
   * and the account shows only once something lies there.
   */
  const places = useMemo(() => {
    const offices = snapshot?.offices ?? [];
    const only = offices.length === 1 ? offices[0] : undefined;
    if (only) {
      const accountUsed = [...pages, ...files, ...folders].some((entry) => entry.spaceId === null);
      return [
        ...(accountUsed ? [{ id: null as string | null, name: t`Whole account` }] : []),
        { id: only.id as string | null, name: accountUsed ? only.name : "" },
      ];
    }
    return [
      { id: null as string | null, name: t`Whole account` },
      ...offices
        .filter((office) => !scopeOffice || office.id === scopeOffice)
        .map((office) => ({ id: office.id as string | null, name: office.name })),
    ];
  }, [snapshot, scopeOffice, pages, files, folders, t]);

  /**
   * Writes the page that was open when the person left its fields. Autosaves
   * are serialized, and each save keeps the page it started for even when the
   * person has already opened another one.
   */
  function save() {
    const current = pageRef.current;
    if (!current) return Promise.resolve();
    const titleInput = titleRef.current;
    const title = titleInput.trim() || current.title;
    const content = draftRef.current;
    const saved = savedRef.current;
    if (saved?.pageId === current.id && saved.title === title && saved.content === content) {
      return Promise.resolve();
    }

    const pageId = current.id;
    const kind = current.kind;
    pendingSavesRef.current += 1;
    setBusy(true);
    saveQueueRef.current = saveQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        try {
          await rpc.booroKnowledge.save({ pageId, title, kind, content });
          if (selectedIdRef.current === pageId) {
            savedRef.current = { pageId, title, content };
          }
          setError("");
          await reload();
          const next = await rpc.booroKnowledge.page({ pageId });
          if (selectedIdRef.current !== pageId) return;
          pageRef.current = next;
          setPage(next);
          if (!next) return;
          if (draftRef.current === content) {
            draftRef.current = next.content;
            setDraft(next.content);
          }
          if (titleRef.current === titleInput) {
            titleRef.current = next.title;
            setPageTitle(next.title);
          }
        } catch (cause) {
          setError(errorText(cause));
        }
      })
      .finally(() => {
        pendingSavesRef.current -= 1;
        if (pendingSavesRef.current === 0) setBusy(false);
      });
    return saveQueueRef.current;
  }

  async function createPage(spaceId: string | null, folder = "") {
    setBusy(true);
    try {
      const name = t`New page`;
      const created = await rpc.booroKnowledge.save({
        spaceId,
        folder,
        title: name,
        kind: "handbuch",
        content: scaffold(name),
      });
      await reload();
      setFileId(null);
      setSelectedId(created.id);
      setMode("write");
      setError("");
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  /** Turns the last change back, mostly one an agent made. */
  async function restorePrevious() {
    if (!page) return;
    setBusy(true);
    try {
      await saveQueueRef.current.catch(() => undefined);
      await rpc.booroKnowledge.restorePrevious({ pageId: page.id });
      await reload();
      await loadPage(page.id);
      setError("");
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  async function removePage() {
    if (!page) return;
    setBusy(true);
    try {
      await saveQueueRef.current.catch(() => undefined);
      await rpc.booroKnowledge.remove({ pageId: page.id });
      pageRef.current = null;
      savedRef.current = null;
      titleRef.current = "";
      draftRef.current = "";
      setSelectedId(null);
      setMode("read");
      setPage(null);
      await reload();
      setError("");
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  /** Runs a change of the folders, then shows the new state or why it failed. */
  async function change(run: () => Promise<unknown>) {
    try {
      await run();
      setError("");
    } catch (cause) {
      setError(errorText(cause));
    }
    await reload();
  }

  function select(item: KnowledgeItem) {
    if (item.kind === "file") {
      setFileId(item.id);
      setSelectedId(null);
      return;
    }
    setFileId(null);
    // Opening another page returns to reading; the new-page action is the one
    // that starts in edit.
    if (item.id !== selectedId) setMode("read");
    setSelectedId(item.id);
  }

  /** Puts files from the computer into a folder, one after another. */
  async function upload(spaceId: string | null, folder: string, chosen: File[]) {
    setUploading((count) => count + chosen.length);
    let last: string | null = null;
    const failed: string[] = [];
    for (const file of chosen) {
      try {
        const created = await rpc.booroKnowledge.uploadFile({
          spaceId,
          folder,
          name: file.name,
          mimeType: file.type,
          contentBase64: await base64Of(file),
        });
        last = created.id;
      } catch (cause) {
        failed.push(`${file.name}: ${errorText(cause)}`);
      } finally {
        setUploading((count) => count - 1);
      }
    }
    setError(failed.join(" · "));
    await reload();
    if (last) select({ kind: "file", id: last });
  }

  /** The pages this one links to, and the links whose page does not exist yet. */
  const linked = useMemo(
    () =>
      (page?.links ?? []).map((link) => ({
        link,
        target: linkedPage(link, pages, page?.spaceId ?? null),
      })),
    [page, pages],
  );
  const unknownLinks = linked.filter((entry) => !entry.target).map((entry) => entry.link);

  /** Draws a link in the text to another page as a way to open it. */
  function renderPageLink(link: string, name: string) {
    const target = linkedPage(link, pages, page?.spaceId ?? null);
    if (!target) return undefined;
    return (
      <button
        type="button"
        className="cursor-pointer text-foreground underline underline-offset-2 hover:text-primary"
        onClick={() => select({ kind: "page", id: target.id })}
      >
        {name}
      </button>
    );
  }

  /** The pages that fit the word the person is writing: the same place and the account. */
  const suggestions = useMemo(() => {
    if (suggestionQuery === null || !page) return [];
    const needle = suggestionQuery.trim().toLowerCase();
    return pages
      .filter((entry) => entry.id !== selectedId)
      .filter((entry) => entry.spaceId === null || entry.spaceId === page.spaceId)
      .filter((entry) => !needle || entry.title.toLowerCase().includes(needle))
      .slice(0, 6);
  }, [suggestionQuery, pages, selectedId, page]);

  /** Puts the link to a page into the text. */
  const insertLink = useCallback(
    (title: string) => {
      const editor = editorRef.current;
      const cursor = editor?.selectionStart ?? draftRef.current.length;
      const next = insertWikiLink(draftRef.current, cursor, title);
      draftRef.current = next.content;
      setDraft(next.content);
      setSuggestionQuery(null);
      requestAnimationFrame(() => {
        editor?.focus();
        editor?.setSelectionRange(next.cursor, next.cursor);
      });
    },
    [draft],
  );

  const placeName = (spaceId: string | null) =>
    spaceId === null
      ? t`Whole account`
      : (snapshot?.offices.find((office) => office.id === spaceId)?.name ?? "");

  /** The agent that wrote the page last; a person's change names nobody. */
  const writerName = page?.updatedByBotId
    ? (snapshot?.agents.find((agent) => agent.id === page.updatedByBotId)?.name ?? t`an agent`)
    : null;

  return (
    <>
      <WorkspaceViewHeader
        title={title}
        chrome={chrome}
        actions={
          !snapshot || snapshot.offices.length === 1 ? (
            // One office: a new page goes there, nothing to choose. Until the
            // offices are known the button waits; without them it uses the account.
            <Button
              size="sm"
              variant="outline"
              disabled={busy || (!snapshot && !orgError)}
              data-testid="knowledge-new"
              onClick={() => void createPage(snapshot?.offices[0]?.id ?? null)}
            >
              <Plus />
              <Trans>New page</Trans>
            </Button>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger
                disabled={busy}
                render={<Button size="sm" variant="outline" data-testid="knowledge-new" />}
              >
                <Plus />
                <Trans>New page</Trans>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuGroup>
                  <DropdownMenuLabel>
                    <Trans>Where should it lie?</Trans>
                  </DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => void createPage(null)}>
                    <Globe />
                    <Trans>Whole account</Trans>
                  </DropdownMenuItem>
                  {(snapshot?.offices ?? []).map((office) => (
                    <DropdownMenuItem key={office.id} onClick={() => void createPage(office.id)}>
                      <Building />
                      <span dir="auto">{office.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          )
        }
      />
      {snapshot && (snapshot.offices.length > 1 || snapshot.agents.length > 1) ? (
        <div className="border-b border-border px-4 py-2">
          <ScopeFilter snapshot={snapshot} scope={scope} onChange={setScope} />
        </div>
      ) : null}
      {error ? (
        <p role="alert" className="px-4 py-2 text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        <aside className="flex w-full shrink-0 flex-col border-b border-border md:w-72 md:border-b-0 md:border-e">
          <div className="p-3">
            <div className="relative">
              <Search
                size={14}
                aria-hidden="true"
                className="pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t`Search pages and files`}
                aria-label={t`Search pages and files`}
                className="ps-8"
              />
            </div>
          </div>
          <div className="rk-scroll flex-1 overflow-y-auto px-2 pb-3">
            <KnowledgeTree
              places={places}
              pages={filtered}
              files={filteredFiles}
              folders={filteredFolders}
              query={query}
              selected={
                fileId
                  ? { kind: "file", id: fileId }
                  : selectedId
                    ? { kind: "page", id: selectedId }
                    : null
              }
              reachIcon={(receivers) => {
                const reach = pageReach(receivers);
                const ReachIcon = REACH_ICONS[reach];
                return (
                  <ReachIcon
                    size={13}
                    strokeWidth={1.8}
                    role="img"
                    aria-label={reachLabels[reach]}
                    className="shrink-0 text-muted-foreground"
                  >
                    <title>{reachLabels[reach]}</title>
                  </ReachIcon>
                );
              }}
              onSelect={select}
              onCreatePage={(spaceId, folder) => void createPage(spaceId, folder)}
              onCreateFolder={(spaceId, path) =>
                change(() => rpc.booroKnowledge.createFolder({ spaceId, path }))
              }
              onRenameFolder={(spaceId, from, to) =>
                change(() => rpc.booroKnowledge.renameFolder({ spaceId, from, to }))
              }
              onRemoveFolder={(spaceId, path) =>
                void change(() => rpc.booroKnowledge.removeFolder({ spaceId, path }))
              }
              onUpload={(spaceId, folder, chosen) => void upload(spaceId, folder, chosen)}
              onMove={(item, folder) =>
                void change(() => rpc.booroKnowledge.move({ kind: item.kind, id: item.id, folder }))
              }
            />
            {pages.length === 0 && files.length === 0 && folders.length === 0 ? (
              <p className="px-2 py-1 text-[13px] text-muted-foreground">
                <Trans>No pages yet</Trans>
              </p>
            ) : null}
          </div>
          {uploading > 0 ? (
            <p
              role="status"
              className="border-t border-border px-4 py-2 text-[12.5px] text-muted-foreground"
            >
              <Trans>Uploading {uploading} files…</Trans>
            </p>
          ) : null}
        </aside>

        <section className="rk-scroll flex min-w-0 flex-1 flex-col overflow-y-auto">
          {fileId ? (
            <KnowledgeFileView
              key={fileId}
              fileId={fileId}
              snapshot={snapshot}
              folders={folders}
              onChanged={reload}
              onRemoved={() => {
                setFileId(null);
                void reload();
              }}
            />
          ) : page ? (
            <>
              <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
                <Input
                  data-testid="knowledge-title"
                  value={pageTitle}
                  onChange={(event) => {
                    titleRef.current = event.target.value;
                    setPageTitle(event.target.value);
                  }}
                  onBlur={() => void save()}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") event.currentTarget.blur();
                  }}
                  aria-label={t`Title`}
                  className="min-w-40 max-w-72 flex-1 border-transparent px-1 text-[15px] font-medium shadow-none focus-visible:border-border"
                />
                <span className="flex items-center gap-1 text-[12px] text-muted-foreground">
                  {page.spaceId === null ? (
                    <Globe size={12} aria-hidden="true" />
                  ) : (
                    <Building size={12} aria-hidden="true" />
                  )}
                  <span dir="auto">{placeName(page.spaceId)}</span>
                  <span>· {page.path}</span>
                  {writerName ? (
                    <span data-testid="knowledge-written-by" dir="auto">
                      · {t`Changed by ${writerName}`}
                    </span>
                  ) : null}
                </span>
                {page.updatedByBotId && page.hasPrevious ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid="knowledge-restore"
                    disabled={busy}
                    onClick={() => void restorePrevious()}
                  >
                    <Undo2 />
                    <Trans>Turn back</Trans>
                  </Button>
                ) : null}
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant={mode === "write" ? "secondary" : "ghost"}
                    aria-pressed={mode === "write"}
                    onClick={() => setMode("write")}
                  >
                    <Pencil />
                    <Trans>Write</Trans>
                  </Button>
                  <Button
                    size="sm"
                    variant={mode === "read" ? "secondary" : "ghost"}
                    aria-pressed={mode === "read"}
                    onClick={() => setMode("read")}
                  >
                    <Eye />
                    <Trans>Read</Trans>
                  </Button>
                  <MoveMenu
                    folders={folders
                      .filter((folder) => folder.spaceId === page.spaceId)
                      .map((folder) => folder.path)}
                    current={page.folder}
                    onMove={(folder) =>
                      void change(async () => {
                        await rpc.booroKnowledge.move({ kind: "page", id: page.id, folder });
                        await loadPage(page.id);
                      })
                    }
                  />
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    aria-label={t`Delete page`}
                    disabled={busy}
                    onClick={() => void removePage()}
                  >
                    <Trash2 />
                  </Button>
                </div>
              </div>

              <AccessRow
                key={page.id}
                resource={{ kind: "page", id: page.id, spaceId: page.spaceId }}
                snapshot={snapshot}
                onChanged={reload}
              />

              {linked.some((entry) => entry.target) ? (
                <p
                  data-testid="knowledge-links"
                  className="flex flex-wrap items-center gap-1.5 border-b border-border px-4 py-2 text-[12.5px] text-muted-foreground"
                >
                  <Link2 size={13} aria-hidden="true" />
                  <Trans>Linked with</Trans>
                  {linked
                    .filter((entry) => entry.target)
                    .map(({ link, target }) => (
                      <button
                        type="button"
                        key={link}
                        className="cursor-pointer rounded-md bg-muted px-1.5 py-0.5 text-foreground hover:bg-accent"
                        onClick={() => target && select({ kind: "page", id: target.id })}
                      >
                        {target?.title}
                      </button>
                    ))}
                </p>
              ) : null}

              {unknownLinks.length > 0 ? (
                <p className="border-b border-border px-4 py-2 text-[12.5px] text-warning">
                  <Trans>Unknown link</Trans> {unknownLinks.join(", ")}
                </p>
              ) : null}

              <div className="relative flex-1 p-4">
                {mode === "write" ? (
                  <>
                    <Textarea
                      ref={editorRef}
                      data-testid="knowledge-editor"
                      value={draft}
                      onChange={(event) => {
                        draftRef.current = event.target.value;
                        setDraft(event.target.value);
                        setSuggestionQuery(
                          wikiLinkQueryAt(event.target.value, event.target.selectionStart),
                        );
                      }}
                      onBlur={() => void save()}
                      onKeyDown={(event) => {
                        if (event.key === "Escape") setSuggestionQuery(null);
                      }}
                      className="min-h-[26rem] font-mono text-[13.5px] leading-relaxed"
                    />
                    {suggestionQuery ? (
                      <ul
                        data-testid="knowledge-suggestions"
                        className="absolute start-6 top-14 z-10 max-h-56 w-72 overflow-y-auto rounded-xl border border-border bg-popover p-1 shadow-lg"
                      >
                        {suggestions.length === 0 ? (
                          <li className="px-2 py-1.5 text-[12.5px] text-muted-foreground">
                            <Trans>No page fits</Trans>
                          </li>
                        ) : (
                          suggestions.map((entry) => (
                            <li key={entry.id}>
                              <button
                                type="button"
                                data-testid={`knowledge-suggestion-${entry.id}`}
                                onClick={() => insertLink(entry.title)}
                                className="w-full rounded-lg px-2 py-1.5 text-start text-[13px] hover:bg-accent"
                              >
                                <span className="block truncate" dir="auto">
                                  {entry.title}
                                </span>
                                <span className="block truncate text-[11.5px] text-muted-foreground">
                                  {entry.path}
                                </span>
                              </button>
                            </li>
                          ))
                        )}
                      </ul>
                    ) : null}
                  </>
                ) : (
                  <article
                    data-testid="knowledge-preview"
                    className="prose prose-sm max-w-none dark:prose-invert"
                  >
                    <ChatMarkdown renderMention={renderPageLink}>
                      {mapPageLinks(page.content, mentionHref)}
                    </ChatMarkdown>
                  </article>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center p-8">
              <p className="text-sm text-muted-foreground">
                <Trans>Choose a page or file, or drop files into a folder</Trans>
              </p>
            </div>
          )}
        </section>
      </div>
    </>
  );
}

/**
 * Who may read a page or a file, as a line of receivers. A receiver holds for
 * everything beneath it. The menu offers only what the place allows:
 * knowledge of an office stays in that office.
 */
export function AccessRow({
  resource,
  snapshot,
  onChanged,
}: {
  resource: { kind: "page" | "file"; id: string; spaceId: string | null };
  snapshot: OrgSnapshot | null;
  onChanged: () => Promise<void>;
}) {
  const { id: resourceId, kind: resourceKind, spaceId: pageSpaceId } = resource;
  const { t } = useLingui();
  const receiverName = useReceiverName(snapshot);
  const [receivers, setReceivers] = useState<AccessSubject[]>([]);
  const [readerIds, setReaderIds] = useState<string[]>([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const access =
        resourceKind === "page"
          ? await rpc.booroKnowledge.access({ pageId: resourceId })
          : await rpc.booroKnowledge.fileAccess({ fileId: resourceId });
      setReceivers(access.receivers);
      setReaderIds(access.readerIds);
      setError("");
    } catch (cause) {
      setError(errorText(cause));
    }
  }, [resourceId, resourceKind]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(next: AccessSubject[]) {
    try {
      if (resourceKind === "page") {
        await rpc.booroKnowledge.setAccess({ pageId: resourceId, receivers: next });
      } else {
        await rpc.booroKnowledge.setFileAccess({ fileId: resourceId, receivers: next });
      }
      setError("");
      await Promise.all([load(), onChanged()]);
    } catch (cause) {
      setError(errorText(cause));
    }
  }

  const has = (receiver: AccessSubject) =>
    receivers.some((entry) => entry.kind === receiver.kind && entry.id === receiver.id);
  const add = (receiver: AccessSubject) => {
    if (!has(receiver)) void save([...receivers, receiver]);
  };
  const remove = (receiver: AccessSubject) =>
    void save(
      receivers.filter((entry) => !(entry.kind === receiver.kind && entry.id === receiver.id)),
    );

  // What the page may be given to depends on where it lies.
  const offices = (snapshot?.offices ?? []).filter(
    (office) => pageSpaceId === null || office.id === pageSpaceId,
  );
  const groups = orderedGroups(snapshot?.groups ?? [], pageSpaceId);
  const agents = (snapshot?.agents ?? []).filter(
    (agent) => pageSpaceId === null || agent.spaceId === pageSpaceId,
  );
  const severalOffices = (snapshot?.offices.length ?? 0) > 1;
  const officeName = (spaceId: string) =>
    snapshot?.offices.find((office) => office.id === spaceId)?.name ?? "";
  const readers = (snapshot?.agents ?? []).filter((agent) => readerIds.includes(agent.id));

  const receiverIcon = (receiver: AccessSubject) => {
    if (receiver.kind === "bot") {
      const agent = snapshot?.agents.find((entry) => entry.id === receiver.id);
      return agent ? <BotAvatar color={agent.color} identity={agent.id} size={16} /> : null;
    }
    const Icon = REACH_ICONS[receiver.kind];
    return <Icon size={12} aria-hidden="true" />;
  };

  return (
    <div
      data-testid="knowledge-access"
      className="space-y-2 border-b border-border px-4 py-3 text-[13px] text-foreground/80"
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="me-1 text-[13px] text-foreground">
          <Trans>Visible to</Trans>
        </span>
        {receivers.length === 0 ? (
          <span className="flex items-center gap-1 text-muted-foreground">
            <Lock size={12} aria-hidden="true" />
            <Trans>Nobody</Trans>
          </span>
        ) : null}
        {receivers.map((receiver) => (
          <span
            key={`${receiver.kind}:${receiver.id}`}
            data-testid={`knowledge-receiver-${receiver.kind}`}
            className="flex items-center gap-1 rounded-full border border-border bg-card py-0.5 ps-2 pe-1"
          >
            {receiverIcon(receiver)}
            <span dir="auto">{receiverName(receiver)}</span>
            <button
              type="button"
              aria-label={t`Remove ${receiverName(receiver)}`}
              className="grid size-4 place-items-center rounded-full text-muted-foreground hover:bg-accent"
              onClick={() => remove(receiver)}
            >
              <X size={11} aria-hidden="true" />
            </button>
          </span>
        ))}
        <DropdownMenu>
          <DropdownMenuTrigger
            render={<Button size="xs" variant="ghost" data-testid="knowledge-add-receiver" />}
          >
            <Plus />
            <Trans>Add</Trans>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="max-h-[min(420px,calc(100vh-16px))] min-w-56 overflow-y-auto"
          >
            {pageSpaceId === null && snapshot ? (
              <DropdownMenuItem
                onClick={() => add({ kind: "account", id: snapshot.organizationId })}
              >
                <Globe />
                <Trans>Whole account</Trans>
              </DropdownMenuItem>
            ) : null}
            {offices.map((office) => (
              <DropdownMenuItem
                key={office.id}
                onClick={() => add({ kind: "space", id: office.id })}
              >
                <Building />
                {pageSpaceId === null ? (
                  <span dir="auto">{office.name}</span>
                ) : (
                  <Trans>Whole office</Trans>
                )}
              </DropdownMenuItem>
            ))}
            {groups.length > 0 ? (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Users />
                  <Trans context="organigram">Group</Trans>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="max-h-[min(420px,calc(100vh-16px))] min-w-56 overflow-y-auto">
                  {groups.map(({ group, depth }) => (
                    <DropdownMenuItem
                      key={group.id}
                      onClick={() => add({ kind: "group", id: group.id })}
                    >
                      <span dir="auto">
                        {`${" ".repeat(depth)}${group.name}${
                          severalOffices && pageSpaceId === null && depth === 0
                            ? ` · ${officeName(group.spaceId)}`
                            : ""
                        }`}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            ) : null}
            {agents.length > 0 ? (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <UserRound />
                  <Trans>Agent</Trans>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="max-h-[min(420px,calc(100vh-16px))] min-w-56 overflow-y-auto">
                  {agents.map((agent) => (
                    <DropdownMenuItem
                      key={agent.id}
                      onClick={() => add({ kind: "bot", id: agent.id })}
                    >
                      <BotAvatar color={agent.color} identity={agent.id} size={16} />
                      <span dir="auto">
                        {severalOffices && pageSpaceId === null
                          ? `${agent.name} · ${officeName(agent.spaceId)}`
                          : agent.name}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            ) : null}
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled={receivers.length === 0} onClick={() => void save([])}>
              <Lock />
              <Trans>Close for everyone</Trans>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <p
        data-testid="knowledge-readers"
        className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground"
      >
        {readers.length === 0 ? (
          resourceKind === "page" ? (
            <Trans>No agent reads this page.</Trans>
          ) : (
            <Trans>No agent uses this file.</Trans>
          )
        ) : (
          <>
            <span className="flex -space-x-1">
              {readers.slice(0, 6).map((agent) => (
                <BotAvatar key={agent.id} color={agent.color} identity={agent.id} size={16} />
              ))}
            </span>
            <span>
              {resourceKind === "file"
                ? readers.length === 1
                  ? t`${readers[0]?.name ?? ""} uses this file.`
                  : t`${readers.length} agents use this file.`
                : readers.length === 1
                  ? t`${readers[0]?.name ?? ""} reads this page.`
                  : t`${readers.length} agents read this page.`}
            </span>
          </>
        )}
      </p>
      {error ? (
        <p role="alert" className="text-[12.5px] text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** The bytes of a chosen file, as base64 without the data URL prefix. */
export function base64Of(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error("Could not read the file."));
    reader.readAsDataURL(file);
  });
}
